import { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useApp } from '../context/AppContext';
import { StageBadge, ExitBadge, AlignmentBadge, ScoreBadge } from '../components/Badges';
import { Input } from '../components/ui/input';
import { Select, SelectTrigger, SelectContent, SelectItem, SelectValue } from '../components/ui/select';
import { Search, ArrowUpDown, BarChart3, Eye, Star, AlertTriangle, Settings, Zap, Building2 } from 'lucide-react';

export default function SignalsModule() {
  const { signals, watchlist, toggleWatchlist, filteredCount, settings, brokerData, instFlow } = useApp();
  const navigate = useNavigate();
  const [search, setSearch] = useState('');
  const [sortKey, setSortKey] = useState('myScore');
  const [sortDir, setSortDir] = useState('desc');
  const [stageFilter, setStageFilter] = useState('ALL');
  const [exitFilter, setExitFilter] = useState('ALL');

  const filtered = useMemo(() => {
    let list = [...signals];
    if (search) {
      const q = search.toUpperCase();
      list = list.filter(s => s.symbol.includes(q));
    }
    if (stageFilter !== 'ALL') {
      list = list.filter(s => s.myStage === stageFilter || s.optStage === stageFilter);
    }
    if (exitFilter !== 'ALL') {
      list = list.filter(s => s.exitMode === exitFilter);
    }
    list.sort((a, b) => {
      const av = a[sortKey] ?? 0;
      const bv = b[sortKey] ?? 0;
      return sortDir === 'asc' ? av - bv : bv - av;
    });
    return list;
  }, [signals, search, sortKey, sortDir, stageFilter, exitFilter]);

  const toggleSort = (key) => {
    if (sortKey === key) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortKey(key); setSortDir('desc'); }
  };

  const SortHeader = ({ label, field }) => (
    <th
      onClick={() => toggleSort(field)}
      className="cursor-pointer select-none hover:text-trading-text transition-colors"
    >
      <span className="flex items-center gap-1">
        {label}
        {sortKey === field && <ArrowUpDown className="w-3 h-3" />}
      </span>
    </th>
  );

  return (
    <div className="p-6" data-testid="signals-module">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="font-heading text-2xl font-semibold tracking-tight flex items-center gap-2">
            <BarChart3 className="w-6 h-6 text-trading-blue" />
            Signals
          </h1>
          <p className="text-sm text-trading-muted mt-1">{filtered.length} stocks in universe</p>
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-3 mb-4">
        <div className="relative flex-1 min-w-[200px] max-w-xs">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-trading-muted" />
          <Input
            data-testid="signal-search"
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder="Search symbol..."
            className="pl-9 bg-trading-bg border-trading-border text-trading-text h-8 text-sm"
          />
        </div>
        <Select value={stageFilter} onValueChange={setStageFilter}>
          <SelectTrigger data-testid="stage-filter" className="w-36 h-8 bg-trading-bg border-trading-border text-xs">
            <SelectValue placeholder="Stage" />
          </SelectTrigger>
          <SelectContent className="bg-trading-surface border-trading-border">
            <SelectItem value="ALL" className="text-xs">All Stages</SelectItem>
            {['CONFIRMED', 'BUILD', 'EARLY', 'WATCH', 'NONE'].map(s => (
              <SelectItem key={s} value={s} className="text-xs">{s}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select value={exitFilter} onValueChange={setExitFilter}>
          <SelectTrigger data-testid="exit-filter" className="w-36 h-8 bg-trading-bg border-trading-border text-xs">
            <SelectValue placeholder="Exit Mode" />
          </SelectTrigger>
          <SelectContent className="bg-trading-surface border-trading-border">
            <SelectItem value="ALL" className="text-xs">All Exit</SelectItem>
            {['NONE', 'WARNING', 'CONFIRMED', 'FORCED_EXIT'].map(s => (
              <SelectItem key={s} value={s} className="text-xs">{s}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Filter Warning */}
      {filteredCount > 0 && signals.length === 0 && brokerData.length > 0 && (
        <div data-testid="filter-warning" className="mb-4 p-3 bg-trading-amber/5 border border-trading-amber/20 rounded-sm flex items-center justify-between">
          <div className="flex items-center gap-2 text-trading-amber text-sm">
            <AlertTriangle className="w-4 h-4 flex-shrink-0" />
            <span>
              <strong>{filteredCount} stock{filteredCount > 1 ? 's' : ''}</strong> filtered out by price limit (max PHP {settings.maxPrice || 5}).
            </span>
          </div>
          <button
            onClick={() => navigate('/settings')}
            className="flex items-center gap-1 text-xs text-trading-blue hover:text-trading-blue/80 transition-colors"
            data-testid="adjust-settings-btn"
          >
            <Settings className="w-3.5 h-3.5" /> Adjust in Settings
          </button>
        </div>
      )}

      {/* Signals Table */}
      {signals.length === 0 ? (
        <EmptyState hasData={brokerData.length > 0} filteredCount={filteredCount} />
      ) : (
        <div className="bg-trading-surface border border-trading-border rounded-sm overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full data-table" data-testid="signals-table">
              <thead>
                <tr className="bg-trading-elevated border-b border-trading-border">
                  <th className="w-8"></th>
                  <SortHeader label="Symbol" field="symbol" />
                  <SortHeader label="Price" field="price" />
                  <SortHeader label="My Score" field="myScore" />
                  <th>My Stage</th>
                  <SortHeader label="Opt Score" field="optScore" />
                  <th>Opt Stage</th>
                  <th>Align</th>
                  <th>Exit</th>
                  <th>Entry</th>
                  <SortHeader label="Liq" field="liquidityScore" />
                  <SortHeader label="Seller" field="sellerRatio" />
                  <SortHeader label="Accel" field="accelerationScore" />
                  <th>Inst Signals</th>
                  <th className="w-8"></th>
                </tr>
              </thead>
              <tbody>
                {filtered.map(s => (
                  <tr
                    key={s.symbol}
                    data-testid={`signal-row-${s.symbol}`}
                    className="border-b border-trading-border/50 hover:bg-trading-elevated/30 cursor-pointer transition-colors"
                    onClick={() => navigate(`/stock/${s.symbol}`)}
                  >
                    <td>
                      <button
                        data-testid={`watchlist-btn-${s.symbol}`}
                        onClick={(e) => { e.stopPropagation(); toggleWatchlist(s.symbol); }}
                        className="p-0.5"
                      >
                        <Star className={`w-3.5 h-3.5 ${watchlist.includes(s.symbol) ? 'fill-trading-amber text-trading-amber' : 'text-trading-muted/40'}`} />
                      </button>
                    </td>
                    <td className="font-semibold text-trading-text">{s.symbol}</td>
                    <td className="text-trading-text">{s.price?.toFixed(2)}</td>
                    <td><ScoreBadge score={s.myScore} /></td>
                    <td><StageBadge stage={s.myStage} /></td>
                    <td><ScoreBadge score={s.optScore} /></td>
                    <td><StageBadge stage={s.optStage} /></td>
                    <td><AlignmentBadge alignment={s.alignment} /></td>
                    <td><ExitBadge exitMode={s.exitMode} /></td>
                    <td>
                      <span className={`font-mono text-[11px] ${s.entryAllowed ? 'text-trading-green' : 'text-trading-red'}`}>
                        {s.entryAllowed ? 'YES' : 'NO'}
                      </span>
                    </td>
                    <td>
                      <span className={`font-mono text-[11px] ${s.liquidityScore >= 0.5 ? 'text-trading-green' : s.liquidityScore >= 0.3 ? 'text-trading-amber' : 'text-trading-red'}`}>
                        {(s.liquidityScore * 100).toFixed(0)}%
                      </span>
                    </td>
                    <td>
                      <span className={`font-mono text-[11px] ${s.sellerRatio > 1.2 ? 'text-trading-red' : s.sellerRatio > 1 ? 'text-trading-amber' : 'text-trading-green'}`}>
                        {s.sellerRatio?.toFixed(2)}
                      </span>
                    </td>
                    <td>
                      <span className={`font-mono text-[11px] ${s.accelerationScore > 0 ? 'text-trading-green' : s.accelerationScore < -0.3 ? 'text-trading-red' : 'text-trading-muted'}`}>
                        {s.accelerationScore > 0 ? '+' : ''}{s.accelerationScore?.toFixed(2)}
                      </span>
                    </td>
                    <td>
                      <div className="flex gap-1">
                        {instFlow[s.symbol]?.largeLowCostSignal && (
                          <span className="px-1 py-0.5 rounded-sm text-[9px] font-mono bg-trading-green/15 text-trading-green border border-trading-green/30" title="Large low-cost institutional position">
                            <Zap className="w-2.5 h-2.5 inline" />
                          </span>
                        )}
                        {instFlow[s.symbol]?.multiInstBuySignal && (
                          <span className="px-1 py-0.5 rounded-sm text-[9px] font-mono bg-trading-blue/15 text-trading-blue border border-trading-blue/30" title={`${instFlow[s.symbol]?.instConsistentBuyers}+ inst buying`}>
                            <Building2 className="w-2.5 h-2.5 inline" />
                          </span>
                        )}
                        {!instFlow[s.symbol]?.largeLowCostSignal && !instFlow[s.symbol]?.multiInstBuySignal && (
                          <span className="text-[10px] text-trading-muted/40">—</span>
                        )}
                      </div>
                    </td>
                    <td>
                      <button
                        data-testid={`view-detail-${s.symbol}`}
                        onClick={(e) => { e.stopPropagation(); navigate(`/stock/${s.symbol}`); }}
                        className="p-0.5 text-trading-muted hover:text-trading-blue transition-colors"
                      >
                        <Eye className="w-3.5 h-3.5" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

function EmptyState({ hasData, filteredCount }) {
  return (
    <div className="bg-trading-surface border border-trading-border rounded-sm p-16 text-center">
      <BarChart3 className="w-12 h-12 text-trading-muted/30 mx-auto mb-4" />
      <h3 className="font-heading text-lg text-trading-text mb-2">
        {hasData && filteredCount > 0 ? 'All Stocks Filtered Out' : 'No Signals Available'}
      </h3>
      <p className="text-sm text-trading-muted max-w-md mx-auto">
        {hasData && filteredCount > 0
          ? `${filteredCount} stock${filteredCount > 1 ? 's' : ''} found but filtered by price/liquidity settings. Increase the Max Price in Settings to see them.`
          : 'Upload broker activity and price data to generate trading signals. Navigate to the Import module to get started.'
        }
      </p>
    </div>
  );
}
